<template>
  <MarketingPageShell>
    <template #hero>
      <span class="hero-badge">帮助中心</span>
      <h1>{{ pageSeo.h1 }}</h1>
      <p>
        笔芯科研帮助中心：学术文档处理流程、检测体系适配、科研点数计费与文稿安全政策。
        以下内容与官网介绍一致，便于检索与分享。
      </p>
      <div class="platform-tags">
        <span v-for="p in platforms" :key="p" class="platform-tag">{{ p }}</span>
      </div>
    </template>

    <nav class="section-nav" aria-label="页面章节">
      <a v-for="item in navSections" :key="item.id" :href="'#' + item.id">{{ item.title }}</a>
    </nav>

    <section id="intro" class="content-section">
      <h3 class="section-heading">平台介绍</h3>
      <p class="section-lead">
        笔芯科研面向科研写作场景，提供降重复率、降AIGC率、双降与学术润色等能力，支持段落试改与docx全文处理。
        科研点数统一账户管理，按字符消耗，提交前可预览预计用量。
      </p>
      <div class="feature-grid">
        <div v-for="item in features" :key="item.title" class="feature-card">
          <i :class="item.icon"></i>
          <h4>{{ item.title }}</h4>
          <p>{{ item.desc }}</p>
        </div>
      </div>
    </section>

    <section id="workflow" class="content-section">
      <h3 class="section-heading">四步完成处理</h3>
      <p class="section-lead">无需复杂配置，从提交到下载全程在线完成。</p>
      <div class="workflow-grid">
        <div v-for="(step, index) in workflowSteps" :key="step.title" class="workflow-card">
          <div class="workflow-num">{{ index + 1 }}</div>
          <i :class="step.icon"></i>
          <h4>{{ step.title }}</h4>
          <p>{{ step.desc }}</p>
        </div>
      </div>
    </section>

    <section class="content-section">
      <h3 class="section-heading">产品亮点</h3>
      <div class="highlight-grid">
        <div v-for="item in highlights" :key="item.title" class="highlight-card">
          <i :class="item.icon"></i>
          <div>
            <h4>{{ item.title }}</h4>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="content-section">
      <h3 class="section-heading">为什么选择笔芯科研</h3>
      <div class="compare-grid">
        <div class="compare-card bad">
          <h4>{{ compare.bad.title }}</h4>
          <ul>
            <li v-for="line in compare.bad.items" :key="line">
              <i class="bi bi-x-circle"></i>{{ line }}
            </li>
          </ul>
        </div>
        <div class="compare-card good">
          <h4>{{ compare.good.title }}</h4>
          <ul>
            <li v-for="line in compare.good.items" :key="line">
              <i class="bi bi-check-circle-fill"></i>{{ line }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <div class="content-section compliance-wrap">
      <AcademicComplianceNotice />
    </div>

    <section id="faq" class="content-section">
      <h3 class="section-heading">常见问题</h3>
      <p class="section-lead">
        仍有疑问？可通过页脚邮箱联系我们，或查看
        <router-link to="/pricing">套餐价格</router-link>
        了解计费详情。
      </p>
      <div class="faq-list">
        <article v-for="item in faqs" :key="item.q" class="faq-item">
          <h4>{{ item.q }}</h4>
          <p>{{ item.a }}</p>
        </article>
      </div>
    </section>

    <div class="page-cta">
      <router-link to="/workbench" class="btn-primary">立即开始体验</router-link>
      <router-link to="/pricing" class="btn-outline">查看套餐价格</router-link>
    </div>
  </MarketingPageShell>
</template>

<script>
import MarketingPageShell from '@/components/portal/MarketingPageShell.vue'
import AcademicComplianceNotice from '@/components/portal/AcademicComplianceNotice.vue'
import {
  MARKETING_COMPARE,
  MARKETING_FAQS,
  MARKETING_FEATURES,
  MARKETING_FAQ_SECTIONS,
  MARKETING_HIGHLIGHTS,
  MARKETING_PLATFORMS,
  MARKETING_WORKFLOW_STEPS
} from '@/constants/marketingFaq'

import { resolvePageSeo } from '@/constants/siteSeo'

export default {
  name: 'FaqView',
  components: { MarketingPageShell, AcademicComplianceNotice },
  computed: {
    pageSeo() {
      return resolvePageSeo('/faq')
    }
  },
  data() {
    return {
      platforms: MARKETING_PLATFORMS,
      features: MARKETING_FEATURES,
      workflowSteps: MARKETING_WORKFLOW_STEPS,
      highlights: MARKETING_HIGHLIGHTS,
      compare: MARKETING_COMPARE,
      faqs: MARKETING_FAQS,
      navSections: MARKETING_FAQ_SECTIONS
    }
  }
}
</script>

<style scoped>
.hero-badge {
  display: inline-block;
  margin-bottom: 12px;
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--primary-light, #e3f2fd);
  color: var(--primary, #1976d2);
  font-size: 12px;
  font-weight: 600;
}

h2 {
  margin: 0 0 12px;
  font-size: clamp(30px, 4vw, 40px);
  font-weight: 700;
}

.page-hero p {
  margin: 0;
  max-width: 640px;
  font-size: 16px;
  line-height: 1.75;
  color: var(--text-secondary, #666);
}

.platform-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
}

.platform-tag {
  padding: 6px 14px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e5e7f5;
  font-size: 13px;
  color: var(--text-secondary, #666);
}

.section-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 36px;
}

.section-nav a {
  padding: 8px 16px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #dbeafe;
  color: var(--primary, #1976d2);
  font-size: 14px;
  text-decoration: none;
}

.section-nav a:hover {
  background: var(--primary-light, #e3f2fd);
}

.content-section {
  margin-bottom: 48px;
}

.section-heading {
  margin: 0 0 10px;
  font-size: 22px;
  font-weight: 700;
}

.section-lead {
  margin: 0 0 24px;
  max-width: 720px;
  font-size: 15px;
  line-height: 1.75;
  color: var(--text-secondary, #666);
}

.section-lead a {
  color: var(--primary, #1976d2);
  text-decoration: none;
}

.section-lead a:hover {
  text-decoration: underline;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.feature-card {
  padding: 22px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #dbeafe;
}

.feature-card i {
  font-size: 22px;
  color: var(--primary, #1976d2);
}

.feature-card h4 {
  margin: 12px 0 8px;
  font-size: 16px;
}

.feature-card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
  color: var(--text-secondary, #666);
}

.workflow-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.workflow-card {
  position: relative;
  padding: 22px 18px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #dbeafe;
  text-align: center;
}

.workflow-num {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--primary-light, #e3f2fd);
  color: var(--primary, #1976d2);
  font-size: 12px;
  font-weight: 700;
  line-height: 24px;
}

.workflow-card i {
  font-size: 26px;
  color: var(--primary, #1976d2);
}

.workflow-card h4 {
  margin: 12px 0 8px;
  font-size: 15px;
}

.workflow-card p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-secondary, #666);
}

.highlight-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.highlight-card {
  display: flex;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #dbeafe;
}

.highlight-card i {
  flex-shrink: 0;
  font-size: 22px;
  color: var(--primary, #1976d2);
}

.highlight-card h4 {
  margin: 0 0 6px;
  font-size: 15px;
}

.highlight-card p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-secondary, #666);
}

.compare-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.compare-card {
  padding: 24px;
  border-radius: 16px;
}

.compare-card.bad {
  background: #fff5f5;
  border: 1px solid #fecaca;
}

.compare-card.good {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.compare-card h4 {
  margin: 0 0 16px;
  font-size: 18px;
}

.compare-card ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.compare-card li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary, #555);
}

.compare-card.bad i {
  color: #ef4444;
}

.compare-card.good i {
  color: #22c55e;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.faq-item {
  padding: 24px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #dbeafe;
}

.faq-item h4 {
  margin: 0 0 10px;
  font-size: 17px;
  line-height: 1.45;
}

.faq-item p {
  margin: 0;
  font-size: 15px;
  line-height: 1.75;
  color: var(--text-secondary, #666);
}

.page-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 8px;
}

.btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border-radius: 10px;
  border: 1px solid #d8dcf0;
  color: var(--text-primary, #333);
  text-decoration: none;
  font-weight: 500;
}

.btn-outline:hover {
  border-color: var(--primary, #1976d2);
  color: var(--primary, #1976d2);
}

.compliance-wrap :deep(.compliance-block) {
  margin-top: 0;
}

@media (max-width: 960px) {
  .workflow-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .feature-grid,
  .highlight-grid,
  .compare-grid {
    grid-template-columns: 1fr;
  }

  .workflow-grid {
    grid-template-columns: 1fr;
  }
}
</style>
